<template>
	<v-app>
		<alerts />
		<env />
		<devops-clinic-indicator v-if="!$vuetify.breakpoint.xs" />
		<call-draggable />
		<app-update-notification />

		<modal
			v-if="openDisconnectDialog"
			v-model="openDisconnectDialog"
			width="600"
			:confirm-text="$t('i-understand')"
			:persistent="true"
			:title="$t('force-disconnect')"
			:type="'question'"
			@confirm="handleSessionDisconnected"
		>
			<v-card-text class="text-body-1">
				{{ $t('disconnect-info') }}
			</v-card-text>
			<v-card-text class="text-body-1 font-weight-bold">
				{{ $t('disconnect-hint') }}
			</v-card-text>
			<v-divider class="mb-4" />
		</modal>

		<microphone-guide-dialog />
		<ios-notification-dialog v-if="connected" />
		<email-disconnected-dialog v-if="connected" />
		<status-overlay :status="status" :is-devops="isDevops" />
		<navigation-menu v-if="nav" @mini="navigationMenuMini = true; mini = true" @expanded="navigationMenuMini = false; mini = false" />
		<status-banner :status="status" :mini="mini" sticky />

		<v-main :class="{ 'pad': !mini }">
			<router-view :key="$route.path" />
		</v-main>
	</v-app>
</template>

<script>
import Vue from "vue";
import { bus } from "@/main.js";
import { mapFields } from "vuex-map-fields";
import { mapState, mapGetters, mapMutations } from "vuex";
import NavigationMenu from "./components/navigation/NavigationMenu.vue";
import { handleLogoutEvent } from "./composables/auth";
import { ALERT_HIDDEN, ALERT_SHOWN, ALERT_BLOCKED, EXTENSION_CALL_PREFIX, PHONE_CALL_STATUS } from "@/common/const";
import { addHeadsetEventListeners } from "@/common/headset";
import useVoice from "@/composables/voice";
import { getAudioDeviceManager } from "@/composables/useAudioDeviceManager";
import CallDraggable from "./components/conversations/call-dialog/CallDraggable.vue";
import MicrophoneGuideDialog from "./components/conversations/call-dialog/MicrophoneGuideDialog.vue";
import IosNotificationDialog from "./components/conversations/chat-box/IosNotificationDialog.vue";
import EmailDisconnectedDialog from "./components/conversations/chat-box/EmailDisconnectedDialog.vue";
import StatusBanner from "./components/support/StatusBanner.vue";
import StatusOverlay from "./components/support/StatusOverlay.vue";
import Modal from "./components/common/Modal.vue";
import AppUpdateNotification from "./components/common/AppUpdateNotification.vue";
import DevopsClinicIndicator from "./components/overlays/DevopsClinicIndicator.vue";
import { createConversation } from "@/composables/conversation";
import { formatToE164 } from "@/common/utils";

export const API_URL = process.env.VUE_APP_API_URL;
export const APP_URL = process.env.VUE_APP_URL;
export const FRENCH_PHONE_NUMBER = process.env.VUE_APP_FRENCH_PHONE_NUMBER;
export const VUE_APP_FRESK_PRIVACY_POLICY_URL = process.env.VUE_APP_FRESK_PRIVACY_POLICY_URL;
export const VUE_APP_FRESK_TERMS_OF_USE_URL = process.env.VUE_APP_FRESK_TERMS_OF_USE_URL;
export const MICROSITE_URL = process.env.VUE_APP_MICROSITE_URL;
export const LOGIN_PATH = "login";
export const AUDIO_DEVICE_NAME = "audioinput";

Vue.mixin({
	computed: {
		...mapFields("conversation", ["showNotificationsAlert"]),
		...mapFields("voice", ["showIncomingCallAlert"]),

		displayNotificationSettings() {
			return (isAlert) => {
				const isIos = /Mac|iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

				if (isAlert) {
					return !isIos && this.showNotificationsAlert === ALERT_SHOWN && this.$vuetify.breakpoint.mdAndUp;
				}

				return !isIos && this.$vuetify.breakpoint.mdAndUp;
			};
		},

		displayHeadsetSettings() {
			return "hid" in navigator;
		},

		displayIncomingCallSettings() {
			return (isAlert) => {
				const isIos = /Mac|iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

				if (isAlert) {
					return !isIos && this.showIncomingCallAlert === ALERT_SHOWN && this.$vuetify.breakpoint.mdAndUp;
				}

				return !isIos && this.$vuetify.breakpoint.mdAndUp;
			};
		}
	}
});

export default {
	name: "App",

	components: {
		StatusBanner,
		StatusOverlay,
		DevopsClinicIndicator,
		NavigationMenu,
		CallDraggable,
		MicrophoneGuideDialog,
		IosNotificationDialog,
		EmailDisconnectedDialog,
		Modal,
		AppUpdateNotification
	},

	data() {
		return {
			mini: true,
			timers: {},
			openDisconnectDialog: false,
			status: {},
			broadcastChannel: null
		};
	},

	metaInfo() {
		if (process.env.NODE_ENV === "production") {
			return {
				meta: [{ name: "google-site-verification", content: "U3DXAYfupUBb8_5KI0A3JQIRdgCX0R7xeIeJ5jBa59E" }]
			};
		}
	},

	computed: {
		...mapState("ui", ["nav", "menu", "fullScreen"]),
		...mapFields("ui", ["navigationMenuMini"]),
		...mapGetters("login", ["profile", "connected", "isDevops", "refreshed"]),
		...mapGetters("clinic", ["clinicData", "country"]),
		...mapGetters("conversation", ["hasPendingNotifications", "unreadConversationCount"]),
		...mapGetters("voice", ["hasIncomingCall", "isOnCall", "isMicroOn", "activeCall", "headsetDevice"]),

		...mapFields("conversation", [
			"browserNotificationsDisabled",
			"desktopNotificationsEnabled",
			"showNotificationsAlert"
		]),

		...mapFields("voice", [
			"showIncomingCallAlert"
		])
	},

	watch: {
		unreadConversationCount() {
			if (navigator && navigator.setAppBadge) {
				navigator.setAppBadge(this.unreadConversationCount);
			}
		},

		hasPendingNotifications: {
			immediate: true,

			handler(hasNotifications) {
				this.handleNotifications(hasNotifications, 5000, 2000, "Fresk 💬", "Fresk", "Nouveau message", "newMessageTimer");
			}
		},

		profile: {
			immediate: true,

			async handler(val, oldValue) {
				if (!val?.id) return;

				// Always keep the UI language in sync with the user's saved profile locale.
				// This must run on every profile change (including same-user re-login/reconnect),
				// so it lives outside the identity guard below.
				this.$i18n.locale = val?.locale || this.$i18n.fallbackLocale;
				this.$root.$i18n.locale = this.$i18n.locale;
				this.$vuetify.lang.current = this.$i18n.locale.substring(0, 2);

				if (val.id === oldValue?.id) return;

				// Re-sync audio device preferences to the (now-cleared) store
				const audioDeviceManager = getAudioDeviceManager();

				await audioDeviceManager.resync().catch((err) => {
					console.warn("[App] Failed to resync audio devices after login:", err.message);
				});
			}
		},

		hasIncomingCall: {
			immediate: true,

			handler(hasIncomingCall) {
				this.handleNotifications(hasIncomingCall, 5000, 2000, "Fresk 📲", "Fresk", "Nouvel appel", "incomingCallTimer");
			}
		},

		isOnCall: {
			immediate: true,

			async handler(isOnCall) {
				if (isOnCall) {
					if (this.isMicroOn) {
						this.enableMicrophone();
					}
				} else {
					if (this.isMicroOn) {
						this.disableMicrophone();
					}
				}
			}
		},

		activeCall: {
			immediate: true,

			handler(activeCall) {
				if (
					activeCall?.callInstance.status() === PHONE_CALL_STATUS.PENDING &&
					!this.connected
				) {
					activeCall.reject();
				}
			}
		}
	},

	async created() {
		bus.$on("start-outbound-call", this.onStartOutboundCall);
		bus.$on("show-conversation", this.onShowConversation);
		bus.$on("refresh-status", this.getStatus);

		if (this.showNotificationsAlert !== ALERT_BLOCKED) {
			this.showNotificationsAlert = ["denied", "default"].includes(Notification.permission)
				? ALERT_SHOWN
				: ALERT_HIDDEN;
		}

		if (this.showIncomingCallAlert !== ALERT_BLOCKED) {
			this.showIncomingCallAlert = this.profile.settings?.allowIncomingCalls === false
				? ALERT_SHOWN
				: ALERT_HIDDEN;
		}

		if (Notification.permission === "denied") this.browserNotificationsDisabled = true;

		if (this.browserNotificationsDisabled) {
			if (Notification.permission === "granted") {
				this.browserNotificationsDisabled = false;
				this.desktopNotificationsEnabled = true;
				this.showNotificationsAlert = ALERT_HIDDEN;
			} else {
				this.desktopNotificationsEnabled = false;
			}
		}

		if ("hid" in navigator) {
			// In case of usb disconnect
			navigator.hid.addEventListener("disconnect", event => {
				if (event.device?.vendorId === 0x6993) {
					this.$store.commit("voice/setHeadsetDevice", undefined);
					this.$store.commit("voice/setHeadsetDeviceReportId", undefined);
					console.debug(`Headset disconnected: ${event.device.productName}`);
				}
			});

			// In case of usb reconnect
			navigator.hid.addEventListener("connect", event => {
				if (event.device?.vendorId === 0x6993) {
					for (let collection of event.device.collections) {
						if (collection.usage === 0x0005 && collection.usagePage === 0x000B) {
							this.setHeadsetDevice(event.device);
							addHeadsetEventListeners(event.device);
							console.debug(`Headset reconnected : ${event.device.productName}`);
						}
					}
				}
			});

			navigator.hid.getDevices().then(devices => {
				if (devices.length == 0) {
					return false;
				}

				devices.forEach(dev => {
					for (let collection of dev.collections) {
						if (collection.usage === 0x0005 && collection.usagePage === 0x000B) {
							this.setHeadsetDevice(dev);
							addHeadsetEventListeners(dev);
						}
					}
				});
			});
		}

		addEventListener("mouseup", () => this.$store.dispatch("login/activity"));
		addEventListener("keyup", () => this.$store.dispatch("login/activity"));
		addEventListener("storage", handleLogoutEvent);

		// Handler for single session events
		bus.$on("disconnected", () => {
			this.openDialog();
		});

		await this.getStatus();

		if (navigator.mediaDevices?.enumerateDevices()) {
			// Initialize the AudioDeviceManager — this handles device enumeration,
			// monitoring, sleep/wake recovery, and device persistence
			const audioDeviceManager = getAudioDeviceManager();

			await audioDeviceManager?.initialize()
				.then(() => {
					console.debug("[App] AudioDeviceManager initialized");
					const hasMic = audioDeviceManager.audioInputs.length > 0;

					this.setIsMicroOn(hasMic);
				}).catch((err) => {
					console.error("[App] Failed to initialize AudioDeviceManager:", err.message);
				});
		}

		window.setInterval(() => {
			this.getStatus();
		}, 120000);
	},

	beforeDestroy() {
		bus.$off("start-outbound-call", this.onStartOutboundCall);
		bus.$off("show-conversation", this.onShowConversation);
		bus.$off("refresh-status", this.getStatus);

		// Clean up broadcast channel
		if (this.broadcastChannel) {
			this.broadcastChannel.close();
		}
	},

	mounted() {},

	methods: {
		...mapMutations("voice", ["setIsMicroOn", "enableMicrophone", "disableMicrophone", "setHeadsetDevice"]),

		handleNotifications(hasNotification, appearTime, clearTime, notificationTitle, title, message, timerName) {
			const setTitle = (title, clearTime) => {
				if (clearTime) {
					setTimeout(() => {
						document.title = title;
					}, clearTime);
				} else {
					document.title = title;
				}
			};

			document.title = hasNotification ? notificationTitle : title;

			if (hasNotification) {
				this.timers[timerName] = setInterval(() => {
					if (hasNotification) {
						document.title = message;
						setTitle(notificationTitle, clearTime);
					}
				}, appearTime + clearTime);
			} else {
				if (this.timers[timerName]) clearInterval(this.timers[timerName]);
				setTitle(title, clearTime);
			}
		},

		openDialog() {
			this.openDisconnectDialog = true;
		},

		async handleSessionDisconnected() {
			this.openDisconnectDialog = false;
			this.$router.go(0);
		},

		async getStatus() {
			try {
				this.status = (await this.axios.get("status")).data;
			} catch (err) {
				console.error("Could not get maintenance status:", err);
			}
		},

		async onStartOutboundCall({ phoneNumber, patient, conversationSid, extension }) {
			if (extension?.id) {
				// Direct call to a transfer extension (external number or partner): no patient conversation
				try {
					await useVoice.outboundCall(`${EXTENSION_CALL_PREFIX}${extension.id}`, {
						fullname: extension.name,
						phone: extension.number
					});
				} catch (err) {
					console.error("Unable to start transfer extension call", err);
					this.$error(this.$t("error.call-error"));
				}
				return;
			}

			// Start the outbound call asynchronously
			const normalizedPhoneNumber = formatToE164(phoneNumber);

			useVoice.outboundCall(normalizedPhoneNumber, patient);

			// Load the conversation related with the call
			if (conversationSid) {
				this.onShowConversation(conversationSid);
				return;
			}

			// Retrieve the conversation or create a new one asynchronously
			createConversation({
				to: normalizedPhoneNumber
			}).then(({ data: conversation }) => {
				if (conversation?.sid) {
					this.onShowConversation(conversation.sid);
				}
			});
		},

		onShowConversation(sid) {
			if (this.$router.currentRoute.name === "conversations") {
				bus.$emit("select-conversation", sid);
				return;
			}

			this.$router.push({
				name: "conversations",
				params: { sid }
			});
		}
	}
};
</script>

<style>
	.pad { margin-left: 56px; }
	.v-banner__actions { align-self: flex-start !important; }

	/* Prevent double-tap zoom on iOS and Android */
	html {
		touch-action: manipulation;
	}

	/* iOS Safari - prevent scroll on mobile */
	@media (max-width: 599px) {
		.v-application--wrap {
			overflow: hidden;
			min-height: 100vh;
			min-height: 100dvh;
		}
		.v-main {
			overflow: hidden;
		}
		.v-main__wrap {
			overflow: hidden;
		}
	}
</style>
